import { buildRoofRails2D } from "building-generation/elements/roofs/roofEdges";
import { touchesBuilding } from "building-generation/elements/roofs/roofNeighbors";
import { makeStartEndRail } from "building-generation/elements/roofs/roofPerimeters";
import {
  getSubRoofSlopes,
  getSubRoofRightSlopes,
  getLeftSpan,
  buildSlopes,
  getSideExtension,
  getHeightAtDistFromEdge,
} from "building-generation/elements/roofs/roofSlopes";
import { checkShedRoofOverlap } from "building-generation/elements/roofTools/shedRoofOverlap";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import { isIndexedRoofPeakDirection } from "building-types/roofs/roofSystemTypes";
import type {
  RoofVolumeGenerationStageInput,
  RoofVolumeGenerationStageResult,
} from "building-types/roofVolumes/roofVolumeGenerationTypes";
import type { SlopeOverlapRange } from "building-types/settings/roofTypes";
import { GableRoofType } from "building-types/settings/settingsTypesEnum";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import { MULTIGABLE_BSP_EPSILON, shedRoofExtensionsNew, slopeOverlapRangeNew } from "doc/utils/roofTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import { isPointInsideBSP } from "geom/bsp";
import { DIST_EPSILON } from "geom/core";
import { line2Tangent } from "geom/line2";
import { offsetPolyLine2 } from "geom/offset";
import { polyLine2Reverse, polyLine2Edge, polyLine2Extend, polyLine2Morph } from "geom/polyLine2";
import { ray2Eval } from "geom/ray2";
import { vec2Dist } from "geom/vec2";

/**
 * Deterimines roof type. This is a trivial work for normal roofs, but for shed roofs
 * we may need to determine the roof type based on the side neighbors,
 */
export function generateRoofVolumeType({
  roofVolumeOptions,
  roofVolume,
}: RoofVolumeGenerationStageInput): RoofVolumeGenerationStageResult {
  const {
    inputPerimeters,
    roofInputSettings,
    clipContext,
    neighborRoofs,
    insertedRoofVolumes,
    mirror,
    roofData,
    flipTruncationOrder,
  } = roofVolumeOptions;
  const {
    roofVersion,
    baseHeight,
    rectangularOverhangs,
    perimeters,
    heelHeightOffsets,
    startTruncation,
    endTruncation,
  } = roofVolume;
  const { roofSideOverrides, roofEdgeOverrides } = roofData;
  const { startOverhang, endOverhang, leftOverhang, rightOverhang } = rectangularOverhangs;
  const { exactPerimeter } = inputPerimeters;

  const { startEdge, boundingPerimeter, offsetBoundingPerimeter } = perimeters;
  const { insideBSP, discardBSP } = clipContext;
  const { roofSettings, wallSettings } = roofInputSettings;
  const { peakDirection, overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING } = roofSettings;

  const exteriorWallOffsetFromOverhangDatum = getExteriorWallOffset(wallSettings, overhangDatum);

  const { startNeighbor, endNeighbor, leftNeighbor, rightNeighbor } = neighborRoofs;
  const leftNeighborRoof = insertedRoofVolumes[leftNeighbor.roofId];
  const rightNeighborRoof = insertedRoofVolumes[rightNeighbor.roofId];
  const startNeighborRoof = insertedRoofVolumes[startNeighbor.roofId];
  const endNeighborRoof = insertedRoofVolumes[endNeighbor.roofId];

  const { slopes: startNeighborSlopes, baseHeight: startRoofBaseHeight } = startNeighbor;
  const { slopes: endNeighborSlopes, baseHeight: endRoofBaseHeight } = endNeighbor;

  let { roofType = GableRoofType.DEFAULT } = roofSettings;

  let leftRail = polyLine2Reverse(polyLine2Edge(offsetBoundingPerimeter, startEdge - 1));
  let rightRail = polyLine2Edge(offsetBoundingPerimeter, startEdge + 1);

  const startRelativeHeight = baseHeight - startRoofBaseHeight;
  const endRelativeHeight = baseHeight - endRoofBaseHeight;

  let span = vec2Dist(leftRail[0], leftRail[1], rightRail[0], rightRail[1]);
  let hasLeftExtension = false;
  let hasRightExtension = false;
  let sideExtension = 0;
  let startOverlap: SlopeOverlapRange | null = null;
  let endOverlap: SlopeOverlapRange | null = null;
  let isUnboundedShedRoof = false;

  const slopeOptions = {
    exactPerimeter,
    boundingPerimeter,
    startEdge,
    roofSettings,
    roofSideOverrides,
    roofEdgeOverrides,
  };

  let leftSideSlopeOptions = getSubRoofSlopes(slopeOptions);
  let rightSideSlopeOptions = getSubRoofRightSlopes(slopeOptions);

  // shed roof sub-types
  if (roofType !== GableRoofType.DEFAULT) {
    const { startRail, endRail } = makeStartEndRail(leftRail, rightRail);

    polyLine2Extend(startRail, leftOverhang, rightOverhang);
    polyLine2Extend(endRail, leftOverhang, rightOverhang);

    const midRail = polyLine2Morph(startRail, endRail, 0.5);
    const testHeight = baseHeight - 1;

    // side connectivity with the building
    const touchesBuildingOnLeft = touchesBuilding({
      node: insideBSP,
      leftRail: startRail,
      rightRail: endRail,
      peakRail: midRail,
      baseHeight: testHeight,
      peakHeight: testHeight,
      leftOverhang: -startOverhang,
      rightOverhang: -startOverhang,
      endSide: false,
      peakOffsetZ: 0,
    });

    const touchesBuildingOnRight = touchesBuilding({
      node: insideBSP,
      leftRail: startRail,
      rightRail: endRail,
      peakRail: midRail,
      baseHeight: testHeight,
      peakHeight: testHeight,
      leftOverhang: -endOverhang,
      rightOverhang: -endOverhang,
      endSide: true,
      peakOffsetZ: 0,
    });

    // determine the shed roof type if it's not specified
    if (roofType === GableRoofType.AUTO_SIDE_SHED_ROOF) {
      if (peakDirection !== undefined && isIndexedRoofPeakDirection[peakDirection]) {
        roofType = GableRoofType.RIGHT_SIDE_SHED_ROOF;
      } else if (leftNeighborRoof && rightNeighborRoof) {
        if (leftNeighborRoof.index === rightNeighborRoof.index) {
          roofType = mirror ? GableRoofType.LEFT_SIDE_SHED_ROOF : GableRoofType.RIGHT_SIDE_SHED_ROOF;
        } else if (leftNeighborRoof.index < rightNeighborRoof.index) {
          roofType = GableRoofType.RIGHT_SIDE_SHED_ROOF;
        } else {
          roofType = GableRoofType.LEFT_SIDE_SHED_ROOF;
        }
      } else if (leftNeighbor && leftNeighborRoof) {
        roofType = GableRoofType.RIGHT_SIDE_SHED_ROOF;
      } else if (rightNeighbor && rightNeighborRoof) {
        roofType = GableRoofType.LEFT_SIDE_SHED_ROOF;
      } else {
        if (touchesBuildingOnLeft) {
          roofType = GableRoofType.RIGHT_SIDE_SHED_ROOF;
        } else if (touchesBuildingOnRight) {
          roofType = GableRoofType.LEFT_SIDE_SHED_ROOF;
        } else {
          roofType = mirror ? GableRoofType.RIGHT_SIDE_SHED_ROOF : GableRoofType.LEFT_SIDE_SHED_ROOF;
        }
      }
    }

    const leftSpan = getLeftSpan({
      leftSideSlopeOptions,
      rightSideSlopeOptions,
      span,
      heelHeightOffsets,
      roofOverhang: leftOverhang,
    });

    const rightSpan = span - leftSpan;
    const halfSpan = span / 2;

    // for avoiding regressions
    if (roofVersion >= 4) {
      leftSideSlopeOptions = getSubRoofSlopes(slopeOptions);
      rightSideSlopeOptions = getSubRoofSlopes(slopeOptions);
    } else {
      if (roofType === GableRoofType.LEFT_SIDE_SHED_ROOF) {
        if (leftSpan < halfSpan) {
          rightSideSlopeOptions = getSubRoofSlopes(slopeOptions);
        }
      } else {
        if (rightSpan < halfSpan) {
          leftSideSlopeOptions = getSubRoofRightSlopes(slopeOptions);
        }
      }
    }

    // check if the shed roof is unbounded (not connected to side neighbors)
    isUnboundedShedRoof =
      roofType === GableRoofType.LEFT_SIDE_SHED_ROOF
        ? !touchesBuildingOnRight && !rightNeighborRoof
        : !touchesBuildingOnLeft && !leftNeighborRoof;

    const lowEaveOverhang = roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? leftOverhang : rightOverhang;
    const highEaveOverhang = roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? rightOverhang : leftOverhang;
    let shedOffset = isUnboundedShedRoof
      ? span - lowEaveOverhang + highEaveOverhang
      : span - leftOverhang - rightOverhang + 2 * exteriorWallOffsetFromOverhangDatum;

    span += shedOffset;

    // look for the host roof from its side neighbors
    const hostNeighborInfo = roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? rightNeighbor : leftNeighbor;
    const hostNeighborRoof = insertedRoofVolumes[hostNeighborInfo.roofId];
    const eaveOverhang = roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? leftOverhang : rightOverhang;

    // build slopes before the snapping
    const tmpSlopes = buildSlopes({
      heelHeightOffsets,
      slopes: roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? leftSideSlopeOptions : rightSideSlopeOptions,
      startNeighborSlopes,
      endNeighborSlopes,
      startTruncation,
      endTruncation,
      flipTruncationOrder,
      startRelativeHeight,
      endRelativeHeight,
      halfSpan: span * 0.5,
      roofOverhang: roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? leftOverhang : rightOverhang,
      startOverlap: startOverlap ?? undefined,
      endOverlap: endOverlap ?? undefined,
    });

    if (hostNeighborRoof) {
      const sideRelativeHeight = baseHeight - hostNeighborInfo.baseHeight;
      let sideRelativeDist = hostNeighborInfo.relativeDist + eaveOverhang - hostNeighborInfo.depth;

      if (hostNeighborInfo.depth > DIST_EPSILON) {
        sideRelativeDist += shedOffset - exteriorWallOffsetFromOverhangDatum;
      }

      sideExtension = getSideExtension(tmpSlopes, hostNeighborInfo.slopes, sideRelativeDist, sideRelativeHeight);

      shedOffset += 2 * sideExtension;
      span += 2 * sideExtension;

      if (roofType === GableRoofType.LEFT_SIDE_SHED_ROOF) {
        hasRightExtension = true;
      } else {
        hasLeftExtension = true;
      }
    }

    const highRail = roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? leftRail : rightRail;
    const lowRail = roofType === GableRoofType.LEFT_SIDE_SHED_ROOF ? rightRail : leftRail;

    // move the opposite rail
    if (roofType === GableRoofType.LEFT_SIDE_SHED_ROOF) {
      rightRail = offsetPolyLine2(rightRail, shedOffset);
    } else {
      leftRail = offsetPolyLine2(leftRail, -shedOffset);
    }

    if (startNeighborRoof) {
      const { hasOverlap, t1 } = checkShedRoofOverlap({
        startIndex: 0,
        lowRail,
        highRail,
        neighborRoofVolume: startNeighborRoof,
      });

      if (hasOverlap && t1 > startNeighborRoof.rectangularOverhangs.startOverhang + DIST_EPSILON) {
        let offsetT = 0;
        const [dirX, dirY] = line2Tangent(lowRail[0], lowRail[1], highRail[0], highRail[1]);
        const z = baseHeight + getHeightAtDistFromEdge(tmpSlopes, t1) + GABLE_ROOF_THICKNESS;
        const [x, y] = ray2Eval(lowRail[0], lowRail[1], dirX, dirY, t1);
        if (!isPointInsideBSP(discardBSP, x, y, z, false, MULTIGABLE_BSP_EPSILON)) {
          offsetT -= startNeighborRoof.rectangularOverhangs.endOverhang;
        }
        startOverlap = slopeOverlapRangeNew(t1 + offsetT, Number.MAX_VALUE);
      }
    }

    if (endNeighborRoof) {
      const { hasOverlap, t1 } = checkShedRoofOverlap({
        startIndex: 2,
        lowRail,
        highRail,
        neighborRoofVolume: endNeighborRoof,
      });

      if (hasOverlap && t1 > endNeighborRoof.rectangularOverhangs.startOverhang + DIST_EPSILON) {
        let offsetT = 0;
        const [dirX, dirY] = line2Tangent(lowRail[2], lowRail[3], highRail[2], highRail[3]);
        const z = baseHeight + getHeightAtDistFromEdge(tmpSlopes, t1) + GABLE_ROOF_THICKNESS;
        const [x, y] = ray2Eval(lowRail[2], lowRail[3], dirX, dirY, t1);
        if (!isPointInsideBSP(discardBSP, x, y, z, false, MULTIGABLE_BSP_EPSILON)) {
          offsetT -= endNeighborRoof.rectangularOverhangs.endOverhang;
        }

        endOverlap = slopeOverlapRangeNew(t1 + offsetT, Number.MAX_VALUE);
      }
    }
  }

  const rails2D = buildRoofRails2D(leftRail, rightRail);

  const shedExtensions = shedRoofExtensionsNew({
    hasLeftExtension,
    hasRightExtension,
    sideExtension,
  });

  return {
    roofVolume: {
      roofType,
      span,
      startOverlap: startOverlap ?? undefined,
      endOverlap: endOverlap ?? undefined,
      rails2D,
      isUnboundedShedRoof,
      shedExtensions,
    },
  };
}
